import { useNavigate, useSearch } from '@tanstack/react-router';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

type UseUrlPaginationOptions = { pageSize?: number };

/**
 * Custom hook for managing pagination state via URL parameters.
 *
 * This hook provides a way to synchronize pagination state with URL parameters,
 * allowing for easy navigation and state management based on the URL.
 *
 * @param {UseUrlPaginationOptions} options - Options for pagination, including page size.
 * @returns An object containing the current page, a function to set the page, and the offset.
 */
export function useUrlPagination({
  pageSize = PAGINATION_PAGE_SIZE_DEFAULT,
}: UseUrlPaginationOptions = {}) {
  const { page = 0 } = useSearch({ strict: false });

  const navigate = useNavigate();

  const setPage = (newPage: number) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: newPage,
      }),
    });
  };

  const offset = page * pageSize;

  return {
    page,
    setPage,
    offset,
  };
}
